import { CalendarEvent } from '@highlight/common'
import { useEffect } from 'react'
import { useGlobalEvent } from '@highlight/ipc/react'
import { useIsTakingNotes } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { Toast, useToast } from '@renderer/overlay/Toaster'
import { useIncognitoMode } from '@renderer/hooks/useIncognitoMode'
import { useOutOfMeetingHours } from '@renderer/overlay/Sidepanel/components/meeting/use-out-of-meeting-hours'
import { Button } from '@highlight/ui'
import { calendarEventToScheduledMeeting } from '@shared/utils/connections/calendars'
import { ADD_HOURS_TO_TRANSCRIBE_CTA, OUT_OF_MEETING_HOURS_COPY, useOpenHoursPaywall } from './out-of-meeting-hours'

interface WaitingForMeetingToastProps {
  meeting: CalendarEvent
  toastId: string
}

export const WaitingForMeetingToast = ({ meeting, toastId }: WaitingForMeetingToastProps) => {
  const isTakingNotes = useIsTakingNotes()
  const { startAudioNote } = useStartAudioNote()
  const { isIncognitoEnabled, disableIncognito } = useIncognitoMode()
  const outOfHours = useOutOfMeetingHours()
  const openHoursPaywall = useOpenHoursPaywall()
  const toast = useToast()

  // Auto-dismiss when recording starts (meeting was detected and recording began)
  useEffect(() => {
    if (isTakingNotes) {
      toast.dismiss(toastId)
    }
  }, [isTakingNotes, toast, toastId])

  // Auto-dismiss when awaiting state is cancelled (timeout or cancelled from main process)
  useGlobalEvent('meeting:awaiting-join-cancelled', () => {
    toast.dismiss(toastId)
  })

  const onRecordManually = async () => {
    if (isIncognitoEnabled) {
      await disableIncognito()
    }
    AudioNoteIpcRenderer.cancelAwaitingMeetingJoin()
    // The meeting the user hit Join on, not whatever call is on screen.
    startAudioNote({ source: 'toast', meeting: calendarEventToScheduledMeeting(meeting) })
    toast.dismiss(toastId)
  }

  // Not dismissed, since dismissing cancels the await. Once hours are bought the
  // copy flips back and the join still auto-starts.
  const onAddHours = () => openHoursPaywall(meeting)

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <Toast.Footer>
          <span>
            {outOfHours
              ? OUT_OF_MEETING_HOURS_COPY
              : isIncognitoEnabled
                ? 'Starting this transcription will turn off incognito mode'
                : 'Transcription will start when you join'}
          </span>
          <Button onClick={outOfHours ? onAddHours : onRecordManually} size="sm" variant="primary">
            {outOfHours ? ADD_HOURS_TO_TRANSCRIBE_CTA : 'Start Transcript'}
          </Button>
        </Toast.Footer>
      </Toast.Content>
    </Toast>
  )
}
